// rackdefinition-api-service.js
import { getErrorRes, getSuccessRes } from "../helpers/api-response";
import { handleErrorMessage } from "../helpers/handle-error-message";
import logger from "../helpers/logger";
import axiosIns from "../common/interceptor/axios-interceptor";

export const fetchRackDefinitions = async () => {
  try {
    const res = await axiosIns.get(`rackstar/rackdefinition`);
    // Filter for active items.
    const filtered = res.data.filter((r) => r.rackDefinition_IsAppActive === 1);
    // Transform each record so it uses a lowercase property.
    const transformed = filtered.map((r) => ({
      ...r,
      // Prefer the uppercase property if it exists, otherwise use the already lowercase one.
      rackDefinition_CombinedDesc:
        r.RackDefinition_CombinedDesc || r.rackDefinition_CombinedDesc || "",
    }));
    // Sort using the new lowercase property.
    const sorted = transformed.sort((a, b) =>
      (a.rackDefinition_CombinedDesc || "").localeCompare(
        b.rackDefinition_CombinedDesc || ""
      )
    );
    return getSuccessRes(sorted, "Rack definitions fetched successfully.");
  } catch (error) {
    throw getErrorRes(error, "Failed to fetch rack definitions.");
  }
};

// New function that returns all rack definitions without filtering by active status
export const fetchAllRackDefinitions = async () => {
  try {
    const res = await axiosIns.get(`rackstar/rackdefinition`);
    // Transform each record so it uses a lowercase property, but don't filter by active status
    const transformed = res.data.map((r) => ({
      ...r,
      // Prefer the uppercase property if it exists, otherwise use the already lowercase one.
      rackDefinition_CombinedDesc:
        r.RackDefinition_CombinedDesc || r.rackDefinition_CombinedDesc || "",
    }));
    // Sort using the new lowercase property.
    const sorted = transformed.sort((a, b) =>
      (a.rackDefinition_CombinedDesc || "").localeCompare(
        b.rackDefinition_CombinedDesc || ""
      )
    );
    return getSuccessRes(sorted, "All rack definitions fetched successfully.");
  } catch (error) {
    throw getErrorRes(error, "Failed to fetch all rack definitions.");
  }
};

//Fetch Rack Definition data
export const fetchRackDefinitionAllData = async () => {
  try {
    const res = await axiosIns.get(`rackstar/rackdefinition`);
    return getSuccessRes(
      res.data || [],
      "Rack Definition data fetched successfully."
    );
  } catch (error) {
    const errMessage = handleErrorMessage(error, true);
    throw getErrorRes(errMessage, `Failed to fetch Rack Definition data.`);
  }
};

// Update an existing Rack Definition entry
export const updateRackDefinitionData = async (id, data, toastId) => {
  try {
    const res = await axiosIns.put(
      `rackstar/rackdefinition/${id}`,
      data
    );
    return getSuccessRes(
      res.data,
      "Rack Definition data updated successfully!"
    );
  } catch (error) {
    logger.error("updateRackDefinitionData", error);
    const errMessage = handleErrorMessage(error, true, toastId);
    throw getErrorRes(errMessage, "Failed to update Rack Definition Data.");
  }
};

//Update multiple rows in datagrid
export const updateRackDefinitionRows = async (editedRows) => {
  try {
    logger.log("Saving edited row data:", editedRows);

    const updates = Object.values(editedRows);

    // Send updates one by one or in parallel
    for (const update of updates) {
      await axiosIns.put(
        `rackstar/rackdefinition/${update.rackDefinition_Key}`,
        update
      );
    }

    return getSuccessRes(null, "Rack definition updated successfully");
  } catch (error) {
    const errMessage = handleErrorMessage(error);
    throw getErrorRes(errMessage, "Failed to update rack definition");
  }
};

//Add a Rack Definition data
export const addRackDefinitionData = async (data, toastId) => {
  try {
    const res = await axiosIns.post(
      `rackstar/rackdefinition`,
      data
    );
    return getSuccessRes(res.data, "Rack Definition data added successfully!");
  } catch (error) {
    logger.error("addRackDefinitionData", error);
    const errMessage = handleErrorMessage(error, true, toastId);
    throw getErrorRes(errMessage, "Failed to add Rack Definition data.");
  }
};
